import React, {
  useContext,
  useState,
  useMemo,
  useCallback,
} from "react";
import {
  Card,
  Table,
  Button,
  Label,
  TextInput,
  Textarea,
  Alert,
} from "flowbite-react";
import { Store } from "../context/store";
import {
  IoMdArrowBack,
  IoMdArrowForward,
  IoIosAddCircle,
} from "react-icons/io";
import { Link } from "react-router-dom";
import { HiExclamation } from "react-icons/hi";
import { useAllPermissions } from "../utils/usePermission";
import DashboardLayout from "../components/layout/DashboardLayout";

// Default state for a single item row
const initialInvoiceBody = {
  item: "",
  description: "",
  amount: 0,
};

function Receipts() {
  // Destructure the invoice state from context
  let store = useContext(Store);
  let [invoice, setInvoice] = store.invoice;

  const [step, setStep] = useState(1); // 1 for Details, 2 for Items
  const [invoiceBody, setInvoiceBody] = useState(initialInvoiceBody);
  const [itemError, setItemError] = useState("");

  const { canWriteReceipt } = useAllPermissions();

  // Use useCallback for stable function reference
  const updateInvoiceHeader = useCallback(
    (key, value) => {
      setInvoice((prevInvoice) => ({
        ...prevInvoice,
        [key]: value,
      }));
    },
    [setInvoice],
  );

  const handleInvoiceBodyChange = useCallback((e) => {
    const { name, value } = e.target;

    const parsedValue = name === "amount" ? Number(value) || 0 : value;

    setInvoiceBody((prev) => ({
      ...prev,
      [name]: parsedValue,
    }));
  }, []);

  const pushToInvoice = () => {
    const { item, amount } = invoiceBody;

    // Validation: Check if key fields are present
    if (!item || !amount) {
      setItemError("Please fill in Item and Amount fields.");
      return;
    }

    const unitAmount = Number(amount) || 0;
    const row = {
      ...invoiceBody,
      quantity: 1,
      ordered: 1,
      rate: unitAmount,
      amount: unitAmount,
    };

    // Add new item row to the invoice state
    setInvoice((prevInvoice) => ({
      ...prevInvoice,
      rows: [...prevInvoice.rows, row],
    }));

    // Reset the input state and error message
    setInvoiceBody(initialInvoiceBody);
    setItemError("");
  };

  const isStepOneValid = useMemo(() => {
    // Basic validation for Step 1
    return (
      invoice.date &&
      // invoice.serial > 0 &&
      invoice.invoiceName &&
      invoice.invoiceAddress
    );
  }, [invoice]);

  return (
    <>
      <title>Create Receipt</title>
      <DashboardLayout active="newReceipt" contentClassName="lg:p-8">
            <h5 className="text-xl font-bold leading-none text-gray-900 dark:text-white">
              🧾 Create New Receipt/Invoice
            </h5>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Step {step} of 2:{" "}
              {step === 1 ? "Header and Address Details" : "Add Items"}
            </p>

            <hr className="my-6 border-gray-200 dark:border-gray-700" />

            {/* Step 1: Header Details */}
            {step === 1 && (
              <div data-aos="slide-left">
                <Card className="p-4 shadow-lg dark:bg-gray-800">
                  <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                    Invoice Details
                  </h3>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Column 1: Core Details (Date, Serial, PO, Project) */}
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="date1" value="Date" />
                          <TextInput
                            id="date1"
                            type="date"
                            required
                            value={invoice.date || ""}
                            onChange={(e) =>
                              updateInvoiceHeader("date", e.target.value)
                            }
                          />
                        </div>
                        {/* <div>
                          <Label htmlFor="serial1" value="Receipt Number" />
                          <TextInput
                            id="serial1"
                            type="number"
                            step="0.01"
                            placeholder="e.g., 1001"
                            required
                            min={1}
                            value={invoice.receiptNumber || ""}
                            onChange={(e) =>
                              updateInvoiceHeader(
                                "receiptNumber",
                                parseInt(e.target.value) || 0
                              )
                            }
                          />
                        </div> */}
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="po1" value="Phone (Optional)" />
                          <TextInput
                            id="po1"
                            type="text"
                            placeholder="Enter Phone Number"
                            value={invoice.phone || ""}
                            onChange={(e) =>
                              updateInvoiceHeader("phone", e.target.value)
                            }
                          />
                        </div>
                        <div>
                          <Label
                            htmlFor="project1"
                            value="Project / Property (Optional)"
                          />
                          <TextInput
                            id="project1"
                            type="text"
                            placeholder="Enter Project / Property Name"
                            value={invoice.project || ""}
                            onChange={(e) =>
                              updateInvoiceHeader("project", e.target.value)
                            }
                          />
                        </div>
                      </div>
                    </div>

                    {/* Column 2: Bill To & Ship To Addresses */}
                    <div className="space-y-4">
                      {/* Bill To */}
                      <div>
                        <Label htmlFor="invoiceName1" value="Bill To: Name" />
                        <TextInput
                          id="invoiceName1"
                          type="text"
                          placeholder="Client Name"
                          required
                          value={invoice.invoiceName || ""}
                          onChange={(e) =>
                            updateInvoiceHeader("invoiceName", e.target.value)
                          }
                        />
                      </div>
                      <div>
                        <Label
                          htmlFor="invoiceAddress1"
                          value="Bill To: Address"
                        />
                        <Textarea
                          id="invoiceAddress1"
                          placeholder="Client Address"
                          required
                          rows={3}
                          value={invoice.invoiceAddress || ""}
                          onChange={(e) =>
                            updateInvoiceHeader(
                              "invoiceAddress",
                              e.target.value,
                            )
                          }
                        />
                      </div>

                      {/* Ship To (Optional, copy from Bill To if not needed) */}
                      <div>
                        <Label
                          htmlFor="shipName1"
                          value="Client Email (Optional)"
                        />
                        <TextInput
                          id="shipName1"
                          type="text"
                          placeholder="Client Email"
                          value={invoice.email || ""}
                          onChange={(e) =>
                            updateInvoiceHeader("email", e.target.value)
                          }
                        />
                      </div>
                      <div>
                        <Label
                          htmlFor="shipAddress1"
                          value="Client Office Address (Optional)"
                        />
                        <TextInput
                          id="shipAddress1"
                          placeholder="Client Office Address"
                          rows={3}
                          value={invoice.officeAddress || ""}
                          onChange={(e) =>
                            updateInvoiceHeader("officeAddress", e.target.value)
                          }
                        />
                      </div>
                    </div>
                  </div>

                  {/* Navigation Button */}
                  <div className="flex justify-end mt-6">
                    <Button
                      onClick={() => setStep(2)}
                      disabled={!isStepOneValid || !canWriteReceipt}
                      color={isStepOneValid ? "blue" : "gray"}
                    >
                      Next: Add Items{" "}
                      <IoMdArrowForward className="ms-2 h-4 w-4" />
                    </Button>
                  </div>
                </Card>
              </div>
            )}

            {/* Step 2: Add Items & Review */}
            {step === 2 && (
              <div data-aos="slide-right">
                <div className="flex flex-wrap gap-3 mb-6 justify-between">
                  <Button color="gray" onClick={() => setStep(1)}>
                    <IoMdArrowBack className="me-2 h-4 w-4" /> Go Back
                  </Button>

                  <Button color="success" as={Link} to="/preview">
                    Preview Receipt ({invoice.rows.length} Items)
                    <IoMdArrowForward className="ms-2 h-4 w-4" />
                  </Button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Item Input Card (Wider on mobile, 1/3 on desktop) */}
                  <Card className="col-span-1 lg:col-span-1 shadow-lg dark:bg-gray-800">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                      Add New Item
                    </h3>

                    {itemError && (
                      <Alert
                        color="failure"
                        icon={HiExclamation}
                        onDismiss={() => setItemError("")}
                      >
                        <span className="font-medium">{itemError}</span>
                      </Alert>
                    )}

                    <div className="flex flex-col gap-4">
                      <div>
                        <Label htmlFor="item1" value="Item Name" />
                        <TextInput
                          id="item1"
                          type="text"
                          name="item"
                          placeholder="e.g., Service Charge"
                          value={invoiceBody.item}
                          onChange={handleInvoiceBodyChange}
                        />
                      </div>

                      {/* Description (Textarea) */}
                      <div>
                        <Label htmlFor="desc1" value="Description (Optional)" />
                        <Textarea
                          id="desc1"
                          name="description"
                          placeholder="Detailed description of the item or service."
                          rows={2}
                          value={invoiceBody.description}
                          onChange={handleInvoiceBodyChange}
                        />
                      </div>

                      <div>
                        <Label htmlFor="Amount1" value="Amount" />
                        <TextInput
                          id="Amount1"
                          type="number"
                          step="0.01"
                          name="amount"
                          placeholder="100.00"
                          min={0}
                          value={invoiceBody.amount || ""}
                          onChange={handleInvoiceBodyChange}
                        />
                      </div>
                    </div>

                    <Button
                      type="button"
                      onClick={pushToInvoice}
                      color="blue"
                      className="mt-4"
                    >
                      <IoIosAddCircle className="me-2 h-5 w-5" />
                      Add to Item List
                    </Button>
                  </Card>

                  {/* Item List / Table (Wider on desktop) */}
                  <Card className="col-span-1 lg:col-span-2 shadow-lg dark:bg-gray-800">
                    <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                      Item List ({invoice.rows.length} Items)
                    </h3>
                    <div className="overflow-x-auto">
                      {invoice.rows.length === 0 ? (
                        <p className="text-center text-gray-500 dark:text-gray-400 p-4">
                          No items added yet.
                        </p>
                      ) : (
                        <Table striped hoverable className="text-sm">
                          <Table.Head>
                            <Table.HeadCell>Item</Table.HeadCell>
                            <Table.HeadCell className="min-w-[150px]">
                              Description
                            </Table.HeadCell>
                            <Table.HeadCell>Amount</Table.HeadCell>
                            <Table.HeadCell>
                              {/* Action column for remove button */}
                            </Table.HeadCell>
                          </Table.Head>
                          <Table.Body className="divide-y">
                            {invoice.rows.map((e, i) => (
                              <Table.Row
                                key={i}
                                className="bg-white dark:border-gray-700 dark:bg-gray-800"
                              >
                                <Table.Cell className="font-medium text-gray-900 dark:text-white">
                                  {e.item}
                                </Table.Cell>
                                <Table.Cell>{e.description}</Table.Cell>
                                <Table.Cell className="font-semibold text-green-600 dark:text-green-400">
                                  ₦{e.amount?.toLocaleString()}
                                </Table.Cell>
                                <Table.Cell>
                                  <Button
                                    size="xs"
                                    color="failure"
                                    onClick={() => {
                                      // Handle removal logic (not provided in original, but good practice)
                                      setInvoice((prev) => ({
                                        ...prev,
                                        rows: prev.rows.filter(
                                          (_, idx) => idx !== i,
                                        ),
                                      }));
                                    }}
                                  >
                                    X
                                  </Button>
                                </Table.Cell>
                              </Table.Row>
                            ))}
                          </Table.Body>
                        </Table>
                      )}
                    </div>
                  </Card>
                </div>
              </div>
            )}
      </DashboardLayout>
    </>
  );
}

export default Receipts;
